<div class="air-container">
  <div class="air-header">
    <h2>Quarterly Narrative Impact Report Builder</h2>
    <p class="air-subtitle">Generate a print-ready quarterly centre report (beneficiary data, education &amp; counselling impact, case studies)</p>
  </div>

  <mat-stepper linear #stepper class="air-stepper" (selectionChange)="onStepChange($event)">

    <!-- ═══════════════════════════════════════════════════════
         STEP 1 — Configuration
    ═══════════════════════════════════════════════════════ -->
    <mat-step [stepControl]="configForm" label="Configuration">
      <form [formGroup]="configForm" class="step-form">
        <h3 class="step-title">Report Configuration</h3>
        <div class="help-note">
          <mat-icon>info</mat-icon>
          <ul>
            <li>Select the <strong>centre</strong>, <strong>financial year</strong> and <strong>quarter</strong> first — every later step (auto-fetch, galleries, existing impact reports) uses them.</li>
            <li>The cover title fills in automatically from the quarter; edit it if needed.</li>
            <li>Pick the cover and contact page photos below, and the placeholder images used to fill blank space in the report.</li>
          </ul>
        </div>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Centre</mat-label>
          <mat-select formControlName="centreCode" (selectionChange)="onCentreChange($event)">
            <mat-option *ngFor="let c of (centres$ | async)" [value]="c.centreCode">
              {{c.centreName}} ({{c.centreCode}})
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Financial Year</mat-label>
          <mat-select formControlName="year" (selectionChange)="onYearChange()">
            <mat-option *ngFor="let y of years" [value]="y">{{y}}-{{y + 1}}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Quarter</mat-label>
          <mat-select formControlName="quarterLabel">
            <mat-option *ngFor="let q of quarterOptions" [value]="q.label">
              {{q.label}} ({{q.fullLabel}})
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Cover Title</mat-label>
          <input matInput formControlName="coverTitle" placeholder="e.g. Q3 2025 REPORT">
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Watermark Style</mat-label>
          <mat-select formControlName="watermarkStyle">
            <mat-option value="wave">Wave</mat-option>
            <mat-option value="circle">Circle</mat-option>
            <mat-option value="none">None</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="photo-upload-section" *ngIf="(coverPhotoLibrary$ | async) as libraryPhotos">
          <p class="upload-label">Cover Photo (front page)</p>
          <p class="photo-note">
            <mat-icon>touch_app</mat-icon>
            <span>Click a photo to select it — the <strong>highlighted</strong> photo is the one shown on the report’s cover.
            Or upload a new one (tag the child first if needed). Use ✕ to delete a photo from the library.</span>
          </p>
          <p class="photo-note">
            <mat-icon>photo_size_select_large</mat-icon>
            <span><strong>Best photo:</strong> portrait (about 2:3), <strong>1200 × 1750 px</strong> (at least 600 × 900 px),
            a cut-out PNG with a transparent background, under ~3 MB. It is shown bottom-right on the cover at up
            to 178 × 148 mm, never cropped; keep little empty space around the people.</span>
          </p>
          <p class="photo-count" *ngIf="libraryPhotos.length">
            Previously used cover photos — click one to reuse it.
          </p>
          <div class="child-photo-gallery" *ngIf="libraryPhotos.length">
            <div
              *ngFor="let cp of libraryPhotos"
              class="child-photo-gallery-thumb"
              [class.is-selected]="cp.photoUrl === coverPhotoUrl"
              [title]="cp.childName || ''"
              (click)="selectCoverPhotoFromGallery(cp.photoUrl)">
              <img [src]="cp.photoUrl" alt="cover photo">
              <span class="thumb-caption" *ngIf="cp.childName">{{cp.childName}}</span>
              <button mat-icon-button class="thumb-remove" type="button" title="Delete from library"
                      (click)="$event.stopPropagation(); removeCoverPhoto(cp)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <mat-form-field appearance="outline" class="field-full">
            <mat-label>Child in Photo (optional)</mat-label>
            <mat-select [disabled]="!configForm.value.centreCode" (selectionChange)="onCoverPhotoChildSelect($event)">
              <mat-option [value]="null">— None —</mat-option>
              <mat-option *ngFor="let ch of (children$ | async)" [value]="ch">
                {{ch.firstName}} {{ch.lastName}} ({{ch.childCode}})
              </mat-option>
            </mat-select>
            <mat-hint *ngIf="!configForm.value.centreCode">Select a centre first</mat-hint>
            <mat-hint *ngIf="configForm.value.centreCode">Tags the next uploaded photo with this child's name &amp; code</mat-hint>
          </mat-form-field>

          <input type="file" accept="image/*" (change)="uploadCoverPhoto($event)" #coverInput hidden>
          <button mat-stroked-button color="accent" type="button" (click)="coverInput.click()" [disabled]="uploadingCoverPhoto">
            <mat-icon>{{uploadingCoverPhoto ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
            {{uploadingCoverPhoto ? 'Uploading…' : 'Upload Cover Photo'}}
          </button>
          <div class="photo-preview-row" *ngIf="coverPhotoUrl && !isCoverPhotoInLibrary(libraryPhotos, coverPhotoUrl)">
            <div class="photo-thumb"><img [src]="coverPhotoUrl" alt="cover"></div>
          </div>
        </div>

        <div class="photo-upload-section" *ngIf="(contactPhotoLibrary$ | async) as libraryPhotos">
          <p class="upload-label">Contact Page Photo</p>
          <p class="photo-note">
            <mat-icon>touch_app</mat-icon>
            <span>Click a photo to select it — the <strong>highlighted</strong> photo is shown on the right side of the
            last (Contact) page. Or upload a new one. Use ✕ to delete a photo from the library.</span>
          </p>
          <p class="photo-note">
            <mat-icon>photo_size_select_large</mat-icon>
            <span><strong>Best photo:</strong> portrait (about 3:4), <strong>1400 × 1900 px</strong> (at least 700 × 950 px),
            under ~3 MB. A cut-out PNG with a transparent background stands in the yellow band (up to 121 × 188 mm);
            any other photo is framed beside the contact details, above the band (up to 108 × 150 mm). Photos are
            never cropped and never cover the contact text; a landscape photo comes out smaller.</span>
          </p>
          <p class="photo-count" *ngIf="libraryPhotos.length">
            Previously uploaded photos — click one to reuse it.
          </p>
          <div class="child-photo-gallery" *ngIf="libraryPhotos.length">
            <div
              *ngFor="let p of libraryPhotos"
              class="child-photo-gallery-thumb"
              [class.is-selected]="p.photoUrl === contactPhotoUrl"
              (click)="selectContactPhotoFromGallery(p.photoUrl)">
              <img [src]="p.photoUrl" alt="contact">
              <button mat-icon-button class="thumb-remove" type="button" title="Delete from library"
                      (click)="$event.stopPropagation(); removeContactPhoto(p)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>
          <input type="file" accept="image/*" (change)="uploadContactPhoto($event)" #contactInput hidden>
          <button mat-stroked-button color="accent" type="button" (click)="contactInput.click()" [disabled]="uploadingContactPhoto">
            <mat-icon>{{uploadingContactPhoto ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
            {{uploadingContactPhoto ? 'Uploading…' : 'Upload Contact Photo'}}
          </button>
          <div class="photo-preview-row" *ngIf="contactPhotoUrl && !isPhotoInLibrary(libraryPhotos, contactPhotoUrl)">
            <div class="photo-thumb"><img [src]="contactPhotoUrl" alt="contact"></div>
          </div>
        </div>

        <div class="photo-upload-section" *ngIf="(placeholderLibrary$ | async) as placeholderLibrary">
          <p class="upload-label">Placeholder Images</p>
          <p class="photo-count">
            Used to fill blank space on every report page except the cover and the back page. Each
            image is used at most once per report — once they run out, the remaining gaps stay blank —
            so select plenty, ideally a mix of shapes (wide banners, landscape, square/portrait).
            Click to toggle.
          </p>
          <p class="photo-count" *ngIf="placeholderLibrary.length">
            <strong>{{selectedPlaceholderPhotos.length}} of {{placeholderLibrary.length}} selected.</strong>
            <span *ngIf="selectedPlaceholderPhotos.length === 1">
              Only one gap in the report can be filled — select or upload more images.
            </span>
            <span *ngIf="selectedPlaceholderPhotos.length === 0">
              None selected — blank space in the report will be left empty.
            </span>
          </p>
          <div class="child-photo-gallery" *ngIf="placeholderLibrary.length">
            <div
              *ngFor="let ph of placeholderLibrary"
              class="child-photo-gallery-thumb"
              [class.is-selected]="selectedPlaceholderPhotos.includes(ph.photoUrl)"
              (click)="togglePlaceholderPhoto(ph.photoUrl)">
              <img [src]="ph.photoUrl" alt="placeholder image">
              <button mat-icon-button class="thumb-remove" type="button"
                      (click)="$event.stopPropagation(); removePlaceholderPhoto(ph)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <input type="file" accept="image/*" multiple (change)="uploadPlaceholderPhotos($event)" #placeholderInput hidden>
          <button mat-stroked-button color="accent" type="button" (click)="placeholderInput.click()" [disabled]="uploadingPlaceholder">
            <mat-icon>{{uploadingPlaceholder ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
            {{uploadingPlaceholder ? 'Uploading…' : 'Upload Placeholder Images'}}
          </button>
        </div>

        <div class="step-actions">
          <button mat-raised-button color="primary" matStepperNext [disabled]="configForm.invalid">
            Next: Beneficiary Stats <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 2 — Beneficiary Stats & Charts
    ═══════════════════════════════════════════════════════ -->
    <mat-step [stepControl]="statsForm" label="Beneficiary Stats">
      <form [formGroup]="statsForm">
        <h3 class="step-title">Beneficiary Data for {{periodLabel}}</h3>
        <div class="help-note">
          <mat-icon>info</mat-icon>
          <ul>
            <li>The table and the Age, Gender and Hospital charts are filled in automatically for the selected centre and quarter.</li>
            <li>Correct any number by editing it; add or remove chart rows with the buttons under each card.</li>
            <li>Anything typed in <strong>Remarks</strong> is printed below the table, above the three charts.</li>
          </ul>
        </div>

        <div class="fetch-status" *ngIf="fetchingStats">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <span>Fetching data for the selected centre and period…</span>
        </div>

        <mat-card class="stats-card">
          <mat-card-title>Beneficiary Table</mat-card-title>
          <mat-card-content>
            <div class="stats-grid">
              <mat-form-field appearance="outline">
                <mat-label>Bed Occupancy (%)</mat-label>
                <input matInput type="number" formControlName="bedOccupancyPercent">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Registration &amp; Admission</mat-label>
                <input matInput type="number" formControlName="admissionCount">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Caregiver Accommodation</mat-label>
                <input matInput type="number" formControlName="caregiverAccommodationCount">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Education Sessions</mat-label>
                <input matInput type="number" formControlName="educationSessionCount">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Counselling Sessions</mat-label>
                <input matInput type="number" formControlName="counsellingSessionCount">
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-card class="stats-card">
          <mat-card-title>Remarks</mat-card-title>
          <mat-card-content>
            <mat-form-field appearance="outline" class="field-full">
              <mat-label>Shown above the three charts (optional)</mat-label>
              <textarea matInput rows="4" [(ngModel)]="beneficiaryRemarks" [ngModelOptions]="{standalone:true}"
                placeholder="Leave a blank line between paragraphs"></textarea>
            </mat-form-field>
          </mat-card-content>
        </mat-card>

        <mat-card class="stats-card">
          <mat-card-title>Age Wise</mat-card-title>
          <mat-card-content>
            <div *ngFor="let row of ageWiseData; let i = index" class="chart-row">
              <mat-form-field appearance="outline"><mat-label>Bucket</mat-label><input matInput [(ngModel)]="row.label" [ngModelOptions]="{standalone:true}"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Count</mat-label><input matInput type="number" [(ngModel)]="row.count" [ngModelOptions]="{standalone:true}"></mat-form-field>
              <button mat-icon-button color="warn" type="button" (click)="removeCountRow(ageWiseData, i)"><mat-icon>remove_circle</mat-icon></button>
            </div>
            <button mat-button type="button" (click)="addCountRow(ageWiseData)"><mat-icon>add</mat-icon> Add Bucket</button>
          </mat-card-content>
        </mat-card>

        <mat-card class="stats-card">
          <mat-card-title>Gender Wise</mat-card-title>
          <mat-card-content>
            <div *ngFor="let row of genderWiseData; let i = index" class="chart-row">
              <mat-form-field appearance="outline"><mat-label>Label</mat-label><input matInput [(ngModel)]="row.label" [ngModelOptions]="{standalone:true}"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Count</mat-label><input matInput type="number" [(ngModel)]="row.count" [ngModelOptions]="{standalone:true}"></mat-form-field>
              <button mat-icon-button color="warn" type="button" (click)="removeCountRow(genderWiseData, i)"><mat-icon>remove_circle</mat-icon></button>
            </div>
            <button mat-button type="button" (click)="addCountRow(genderWiseData)"><mat-icon>add</mat-icon> Add Row</button>
          </mat-card-content>
        </mat-card>

        <mat-card class="stats-card">
          <mat-card-title>Hospital Wise</mat-card-title>
          <mat-card-content>
            <div *ngFor="let row of hospitalWiseData; let i = index" class="chart-row">
              <mat-form-field appearance="outline"><mat-label>Hospital</mat-label><input matInput [(ngModel)]="row.label" [ngModelOptions]="{standalone:true}"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Count</mat-label><input matInput type="number" [(ngModel)]="row.count" [ngModelOptions]="{standalone:true}"></mat-form-field>
              <button mat-icon-button color="warn" type="button" (click)="removeCountRow(hospitalWiseData, i)"><mat-icon>remove_circle</mat-icon></button>
            </div>
            <button mat-button type="button" (click)="addCountRow(hospitalWiseData)"><mat-icon>add</mat-icon> Add Hospital</button>
          </mat-card-content>
        </mat-card>

        <div class="step-actions">
          <button mat-button matStepperPrevious type="button">Back</button>
          <button mat-raised-button color="primary" matStepperNext type="button" [disabled]="statsForm.invalid">
            Next: Education Impact <mat-icon>school</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 3 — Education Impact
    ═══════════════════════════════════════════════════════ -->
    <mat-step [stepControl]="eduForm" label="Education Impact">
      <form [formGroup]="eduForm">
        <h3 class="step-title">Non-Formal Education Impact Report</h3>
        <div class="help-note">
          <mat-icon>info</mat-icon>
          <ul>
            <li>Click <strong>Fetch Existing Education Report</strong> to load the highlights, session counts, progress scores and photos from the Education team’s saved impact report for this quarter.</li>
            <li>Every text box and number below can be edited. Use <strong>Generate AI Highlights</strong> for new bullet points if needed.</li>
            <li>Section title, table columns, chart and gallery headings can be changed in <strong>Headings &amp; Labels</strong> at the bottom.</li>
          </ul>
        </div>

        <mat-card class="narrative-card">
          <mat-card-title><mat-icon>history_edu</mat-icon> Existing Education Impact Report</mat-card-title>
          <mat-card-content>
            <p class="photo-count">
              Loads the Education department's already-saved impact report(s) for the selected centre
              and quarter into this step (highlights/text and photos), replacing what is there now.
            </p>
            <button mat-raised-button color="accent" type="button" (click)="fetchExistingEducationReports()" [disabled]="fetchingEduReports">
              <mat-icon>{{fetchingEduReports ? 'hourglass_empty' : 'cloud_download'}}</mat-icon>
              {{fetchingEduReports ? 'Fetching...' : 'Fetch Existing Education Report'}}
            </button>
            <p class="photo-count" *ngFor="let r of eduImpactReports">
              {{r.reportType || 'Report'}} · {{impactReportRangeLabel(r)}} · {{r.submitType || 'draft'}}
            </p>
          </mat-card-content>
        </mat-card>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Intro Text</mat-label>
          <textarea matInput rows="4" formControlName="introText"></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Age Range Text</mat-label>
          <input matInput formControlName="ageRangeText">
        </mat-form-field>
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Toddler / Volunteer Text</mat-label>
          <textarea matInput rows="3" formControlName="toddlerText"></textarea>
        </mat-form-field>

        <div class="stats-grid">
          <mat-form-field appearance="outline"><mat-label>Teacher Sessions</mat-label><input matInput type="number" formControlName="teacherSessions"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Volunteer Sessions</mat-label><input matInput type="number" formControlName="volunteerSessions"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Special Days Observed</mat-label><input matInput type="number" formControlName="specialDaysObserved"></mat-form-field>
        </div>

        <mat-card class="narrative-card">
          <mat-card-title><mat-icon>emoji_events</mat-icon> Highlights</mat-card-title>
          <mat-card-content>
            <mat-form-field appearance="outline" class="field-full">
              <mat-label>Raw notes about specific children / special days (context for AI)</mat-label>
              <textarea matInput rows="3" [(ngModel)]="educationChildNotes" [ngModelOptions]="{standalone:true}"
                placeholder="e.g. Ananya, 5yo, loves puzzles and block games... World Cotton Day celebrated Oct 7th..."></textarea>
            </mat-form-field>
            <button mat-raised-button color="accent" type="button" (click)="generateEducationHighlights()" [disabled]="generatingEduHighlights">
              <mat-icon>{{generatingEduHighlights ? 'hourglass_empty' : 'auto_awesome'}}</mat-icon>
              {{generatingEduHighlights ? 'Generating...' : 'Generate AI Highlights'}}
            </button>

            <div *ngFor="let h of educationHighlights; let i = index" class="bullet-row">
              <mat-form-field appearance="outline" class="field-full">
                <textarea matInput rows="2" [(ngModel)]="educationHighlights[i]" [ngModelOptions]="{standalone:true}"></textarea>
              </mat-form-field>
              <button mat-icon-button color="warn" type="button" (click)="removeEduHighlight(i)"><mat-icon>remove_circle</mat-icon></button>
            </div>
            <button mat-button type="button" (click)="addEduHighlight()"><mat-icon>add</mat-icon> Add Bullet</button>
          </mat-card-content>
        </mat-card>

        <mat-card class="narrative-card">
          <mat-card-title><mat-icon>bar_chart</mat-icon> Progress Chart (Baseline vs Discharge)</mat-card-title>
          <mat-card-content>
            <div class="stats-grid">
              <mat-form-field appearance="outline"><mat-label>Language Baseline</mat-label><input matInput type="number" formControlName="langBaseline"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Language Discharge</mat-label><input matInput type="number" formControlName="langDischarge"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Maths Baseline</mat-label><input matInput type="number" formControlName="mathsBaseline"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Maths Discharge</mat-label><input matInput type="number" formControlName="mathsDischarge"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>EVS/GK Baseline</mat-label><input matInput type="number" formControlName="evsBaseline"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>EVS/GK Discharge</mat-label><input matInput type="number" formControlName="evsDischarge"></mat-form-field>
            </div>
            <mat-form-field appearance="outline" class="field-full mt-8">
              <mat-label>Progress Note</mat-label>
              <textarea matInput rows="2" formControlName="progressNote"></textarea>
            </mat-form-field>
          </mat-card-content>
        </mat-card>

        <mat-card class="narrative-card" *ngFor="let g of [{key:'session', title:'Educational Session Photos', list: eduSessionPhotos, collageUrl: eduSessionCollageUrl}, {key:'activity', title:'Educational Activity Photos', list: eduActivityPhotos, collageUrl: eduActivityCollageUrl}, {key:'volunteer', title:'Education Volunteer Photos', list: eduVolunteerPhotos, collageUrl: eduVolunteerCollageUrl}]">
          <mat-card-title>{{g.title}}</mat-card-title>
          <mat-card-content>
            <p class="photo-note">
              <mat-icon>dashboard_customize</mat-icon>
              <span>Add photos with <strong>Pick from Gallery</strong> or <strong>Upload from Device</strong>. For a good collage,
              select <strong>minimum 4 to 8 images</strong>, then click <strong>Generate Collage</strong>.</span>
            </p>
            <div class="photo-upload-section">
              <button mat-stroked-button color="primary" type="button" (click)="openEduGallery(g.key)">
                <mat-icon>photo_library</mat-icon> Pick from Education Gallery
              </button>
              <input type="file" multiple accept="image/*" (change)="uploadEduPhotos($event, g.key)" #eduFileInput hidden>
              <button mat-stroked-button color="accent" type="button" (click)="eduFileInput.click()">
                <mat-icon>upload_file</mat-icon> Upload from Device
              </button>
              <button mat-stroked-button color="primary" type="button" [disabled]="g.list.length < 2"
                      (click)="openEduCollageEditor(g.key, g.title)">
                <mat-icon>dashboard_customize</mat-icon> {{g.collageUrl ? 'Regenerate' : 'Generate'}} Collage
              </button>
              <span class="photo-count" *ngIf="g.list.length">{{g.list.length}} photo(s)</span>
              <div class="photo-thumb collage-preview" *ngIf="g.collageUrl">
                <img [src]="g.collageUrl" alt="collage preview">
                <span class="collage-badge">Collage</span>
              </div>
              <div class="photo-preview-row">
                <div *ngFor="let url of g.list; let pi = index" class="photo-thumb">
                  <img [src]="url" alt="photo">
                  <button mat-icon-button color="warn" class="remove-photo" type="button" (click)="removeEduPhoto(g.key, pi)">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-expansion-panel class="narrative-card">
          <mat-expansion-panel-header>
            <mat-panel-title><mat-icon>title</mat-icon>&nbsp;Headings &amp; Labels</mat-panel-title>
            <mat-panel-description>Section title, table columns, chart and gallery headings</mat-panel-description>
          </mat-expansion-panel-header>
          <div formGroupName="headings" class="stats-grid">
              <mat-form-field appearance="outline"><mat-label>Section Title</mat-label><input matInput formControlName="title"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Teacher column</mat-label><input matInput formControlName="teacherCol"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Volunteer column</mat-label><input matInput formControlName="volunteerCol"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Special Days column</mat-label><input matInput formControlName="specialDaysCol"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Total column</mat-label><input matInput formControlName="totalCol"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Highlights heading</mat-label><input matInput formControlName="highlightsHeading"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Progress Chart title</mat-label><input matInput formControlName="progressChartTitle"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Session gallery heading</mat-label><input matInput formControlName="sessionGallery"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Activity gallery heading</mat-label><input matInput formControlName="activityGallery"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Volunteer gallery heading</mat-label><input matInput formControlName="volunteerGallery"></mat-form-field>
          </div>
          <div class="stats-grid">
              <mat-form-field appearance="outline"><mat-label>Chart subject 1</mat-label><input matInput formControlName="langLabel"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Chart subject 2</mat-label><input matInput formControlName="mathsLabel"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Chart subject 3</mat-label><input matInput formControlName="evsLabel"></mat-form-field>
          </div>
        </mat-expansion-panel>

        <div class="step-actions">
          <button mat-button matStepperPrevious type="button">Back</button>
          <button mat-raised-button color="primary" matStepperNext type="button">
            Next: Counselling Impact <mat-icon>psychology</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 4 — Counselling Impact
    ═══════════════════════════════════════════════════════ -->
    <mat-step [stepControl]="cslForm" label="Counselling Impact">
      <form [formGroup]="cslForm">
        <h3 class="step-title">Counselling Impact Report</h3>
        <div class="help-note">
          <mat-icon>info</mat-icon>
          <ul>
            <li>Click <strong>Fetch Existing Counselling Report</strong> to load the text, highlights, session counts and photos from the Counselling team’s saved impact report for this quarter.</li>
            <li>Every text box and number below can be edited, including the coping chart percentages and the interpretation bullets.</li>
            <li>Section title, table columns, chart and gallery headings can be changed in <strong>Headings &amp; Labels</strong> at the bottom.</li>
          </ul>
        </div>

        <mat-card class="narrative-card">
          <mat-card-title><mat-icon>history_edu</mat-icon> Existing Counselling Impact Report</mat-card-title>
          <mat-card-content>
            <p class="photo-count">
              Loads the Counselling department's already-saved impact report(s) for the selected centre
              and quarter into this step (highlights/text and photos), replacing what is there now.
            </p>
            <button mat-raised-button color="accent" type="button" (click)="fetchExistingCounsellingReports()" [disabled]="fetchingCslReports">
              <mat-icon>{{fetchingCslReports ? 'hourglass_empty' : 'cloud_download'}}</mat-icon>
              {{fetchingCslReports ? 'Fetching...' : 'Fetch Existing Counselling Report'}}
            </button>
            <p class="photo-count" *ngFor="let r of cslImpactReports">
              {{r.reportType || 'Report'}} · {{impactReportRangeLabel(r)}} · {{r.submitType || 'draft'}}
            </p>
          </mat-card-content>
        </mat-card>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Opening Quote</mat-label>
          <input matInput formControlName="openingQuote">
        </mat-form-field>
        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Intro Paragraphs (blank line between paragraphs)</mat-label>
          <textarea matInput rows="4" formControlName="introParagraphs"></textarea>
        </mat-form-field>

        <div class="stats-grid">
          <mat-form-field appearance="outline"><mat-label>Individual Sessions</mat-label><input matInput type="number" formControlName="individualSessions"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Psychological Interventions</mat-label><input matInput type="number" formControlName="psychologicalInterventions"></mat-form-field>
          <mat-form-field appearance="outline"><mat-label>Group Sessions</mat-label><input matInput type="number" formControlName="groupSessions"></mat-form-field>
        </div>

        <mat-card class="narrative-card">
          <mat-card-title><mat-icon>emoji_events</mat-icon> Highlights</mat-card-title>
          <mat-card-content>
            <mat-form-field appearance="outline" class="field-full">
              <mat-label>Raw notes on concerns / interventions this quarter (context for AI)</mat-label>
              <textarea matInput rows="3" [(ngModel)]="counsellingConcernNotes" [ngModelOptions]="{standalone:true}"
                placeholder="e.g. Anxiety about diagnosis, feeding stress, behavioural issues..."></textarea>
            </mat-form-field>

            <div class="generate-all-bar">
              <button mat-raised-button color="accent" type="button" (click)="generateProblemsAddressed()" [disabled]="generatingProblemsParagraph">
                <mat-icon>{{generatingProblemsParagraph ? 'hourglass_empty' : 'auto_awesome'}}</mat-icon>
                {{generatingProblemsParagraph ? 'Generating...' : 'Generate "Problems Addressed" Paragraph'}}
              </button>
              <button mat-raised-button color="accent" type="button" (click)="generateCounsellingHighlights()" [disabled]="generatingCslHighlights">
                <mat-icon>{{generatingCslHighlights ? 'hourglass_empty' : 'auto_awesome'}}</mat-icon>
                {{generatingCslHighlights ? 'Generating...' : 'Generate Highlight Bullets'}}
              </button>
            </div>

            <mat-form-field appearance="outline" class="field-full">
              <mat-label>Problems Addressed in Counselling</mat-label>
              <textarea matInput rows="4" [(ngModel)]="problemsAddressedParagraph" [ngModelOptions]="{standalone:true}"></textarea>
            </mat-form-field>

            <div *ngFor="let h of counsellingHighlights; let i = index" class="bullet-row">
              <mat-form-field appearance="outline" class="field-full">
                <textarea matInput rows="2" [(ngModel)]="counsellingHighlights[i]" [ngModelOptions]="{standalone:true}"></textarea>
              </mat-form-field>
              <button mat-icon-button color="warn" type="button" (click)="removeCslHighlight(i)"><mat-icon>remove_circle</mat-icon></button>
            </div>
            <button mat-button type="button" (click)="addCslHighlight()"><mat-icon>add</mat-icon> Add Bullet</button>
          </mat-card-content>
        </mat-card>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Interventions and Tools Used</mat-label>
          <textarea matInput rows="4" formControlName="interventionsToolsParagraph"></textarea>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Admissions / Discharge Paragraphs (blank line between paragraphs)</mat-label>
          <textarea matInput rows="3" formControlName="admissionsDischargeParagraphs"
            placeholder="e.g. We have had 6 Admissions in the past quarter..."></textarea>
        </mat-form-field>

        <mat-card class="narrative-card">
          <mat-card-title><mat-icon>pie_chart</mat-icon> Coping Assessment (%)</mat-card-title>
          <mat-card-content>
            <p class="upload-label">Admission Psychological Assessment</p>
            <div *ngFor="let seg of admissionCoping" class="chart-row">
              <mat-form-field appearance="outline"><mat-label>Label</mat-label><input matInput [(ngModel)]="seg.label" [ngModelOptions]="{standalone:true}"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>%</mat-label><input matInput type="number" [(ngModel)]="seg.value" [ngModelOptions]="{standalone:true}"></mat-form-field>
            </div>
            <p class="upload-label mt-8">Discharge Psychological Assessment</p>
            <div *ngFor="let seg of dischargeCoping" class="chart-row">
              <mat-form-field appearance="outline"><mat-label>Label</mat-label><input matInput [(ngModel)]="seg.label" [ngModelOptions]="{standalone:true}"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>%</mat-label><input matInput type="number" [(ngModel)]="seg.value" [ngModelOptions]="{standalone:true}"></mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Interpretation from Graph (one bullet per line)</mat-label>
          <textarea matInput rows="4" formControlName="interpretationBullets"></textarea>
        </mat-form-field>

        <mat-form-field appearance="outline" class="field-full">
          <mat-label>Closing Paragraph (currently staying families, session frequency, etc.)</mat-label>
          <textarea matInput rows="3" formControlName="closingParagraph"></textarea>
        </mat-form-field>

        <mat-card class="narrative-card" *ngFor="let g of [{key:'session', title:'Counselling Session Photos', list: cslSessionPhotos, collageUrl: cslSessionCollageUrl}, {key:'group', title:'Group Counselling Photos', list: cslGroupPhotos, collageUrl: cslGroupCollageUrl}]">
          <mat-card-title>{{g.title}}</mat-card-title>
          <mat-card-content>
            <p class="photo-note">
              <mat-icon>dashboard_customize</mat-icon>
              <span>Add photos with <strong>Pick from Gallery</strong> or <strong>Upload from Device</strong>. For a good collage,
              select <strong>minimum 4 to 8 images</strong>, then click <strong>Generate Collage</strong>.</span>
            </p>
            <div class="photo-upload-section">
              <button mat-stroked-button color="primary" type="button" (click)="openCslGallery(g.key)">
                <mat-icon>photo_library</mat-icon> Pick from Counselling Gallery
              </button>
              <input type="file" multiple accept="image/*" (change)="uploadCslPhotos($event, g.key)" #cslFileInput hidden>
              <button mat-stroked-button color="accent" type="button" (click)="cslFileInput.click()">
                <mat-icon>upload_file</mat-icon> Upload from Device
              </button>
              <button mat-stroked-button color="primary" type="button" [disabled]="g.list.length < 2"
                      (click)="openCslCollageEditor(g.key, g.title)">
                <mat-icon>dashboard_customize</mat-icon> {{g.collageUrl ? 'Regenerate' : 'Generate'}} Collage
              </button>
              <span class="photo-count" *ngIf="g.list.length">{{g.list.length}} photo(s)</span>
              <div class="photo-thumb collage-preview" *ngIf="g.collageUrl">
                <img [src]="g.collageUrl" alt="collage preview">
                <span class="collage-badge">Collage</span>
              </div>
              <div class="photo-preview-row">
                <div *ngFor="let url of g.list; let pi = index" class="photo-thumb">
                  <img [src]="url" alt="photo">
                  <button mat-icon-button color="warn" class="remove-photo" type="button" (click)="removeCslPhoto(g.key, pi)">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-expansion-panel class="narrative-card">
          <mat-expansion-panel-header>
            <mat-panel-title><mat-icon>title</mat-icon>&nbsp;Headings &amp; Labels</mat-panel-title>
            <mat-panel-description>Section title, table columns, chart and gallery headings</mat-panel-description>
          </mat-expansion-panel-header>
          <div formGroupName="headings" class="stats-grid">
              <mat-form-field appearance="outline"><mat-label>Section Title</mat-label><input matInput formControlName="title"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Individual column</mat-label><input matInput formControlName="individualCol"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Interventions column</mat-label><input matInput formControlName="interventionsCol"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Group column</mat-label><input matInput formControlName="groupCol"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Total column</mat-label><input matInput formControlName="totalCol"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Highlights heading</mat-label><input matInput formControlName="highlightsHeading"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>"Problems Addressed" label</mat-label><input matInput formControlName="problemsAddressedLabel"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>"Interventions & Tools" label</mat-label><input matInput formControlName="interventionsToolsLabel"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Admission chart title</mat-label><input matInput formControlName="admissionChartTitle"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Discharge chart title</mat-label><input matInput formControlName="dischargeChartTitle"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Interpretation heading</mat-label><input matInput formControlName="interpretationHeading"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Session gallery heading</mat-label><input matInput formControlName="sessionGallery"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Group gallery heading</mat-label><input matInput formControlName="groupGallery"></mat-form-field>
          </div>
        </mat-expansion-panel>

        <div class="step-actions">
          <button mat-button matStepperPrevious type="button">Back</button>
          <button mat-raised-button color="primary" matStepperNext type="button">
            Next: Case Studies <mat-icon>person</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 5 — Case Studies
    ═══════════════════════════════════════════════════════ -->
    <mat-step label="Case Studies">
      <h3 class="step-title">Braveheart Case Studies</h3>
      <div class="help-note">
        <mat-icon>info</mat-icon>
        <ul>
          <li>Click <strong>Add Case Study</strong> for each child to feature; each case study gets its own page in the report.</li>
          <li>Fill in the child’s details and background notes, then generate the story with AI or write the paragraphs yourself.</li>
          <li>Upload one photo of the child for the case study page.</li>
        </ul>
      </div>

      <button mat-raised-button color="accent" type="button" (click)="addCaseStudy()">
        <mat-icon>add</mat-icon> Add Case Study
      </button>

      <div *ngFor="let cs of caseStudies; let ci = index" class="case-study-form">
        <mat-card class="cs-card">
          <mat-card-title>
            {{cs.name || 'New Case Study'}}
            <button mat-icon-button color="warn" type="button" (click)="removeCaseStudy(ci)" style="margin-left:auto;">
              <mat-icon>delete</mat-icon>
            </button>
          </mat-card-title>
          <mat-card-content>

            <mat-form-field appearance="outline" class="field-full">
              <mat-label>Select Child (auto-fills profile &amp; suggests photo)</mat-label>
              <mat-select [disabled]="!configForm.value.centreCode" (selectionChange)="onCaseStudyChildSelect($event, cs)">
                <mat-option *ngFor="let ch of (children$ | async)" [value]="ch">
                  {{ch.firstName}} {{ch.lastName}} ({{ch.childCode}})
                </mat-option>
              </mat-select>
              <mat-hint *ngIf="!configForm.value.centreCode">Select a centre in Step 1 first</mat-hint>
            </mat-form-field>

            <div class="cs-profile-grid">
              <mat-form-field appearance="outline" class="field-full">
                <mat-label>Braveheart Label</mat-label>
                <input matInput [(ngModel)]="cs.braveheartLabel" [ngModelOptions]="{standalone:true}" placeholder="e.g. Braveheart Kartik Chavan">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Name</mat-label>
                <input matInput [(ngModel)]="cs.name" [ngModelOptions]="{standalone:true}">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Gender</mat-label>
                <input matInput [(ngModel)]="cs.gender" [ngModelOptions]="{standalone:true}">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Age</mat-label>
                <input matInput type="number" [(ngModel)]="cs.age" [ngModelOptions]="{standalone:true}">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>DOB</mat-label>
                <input matInput [(ngModel)]="cs.dob" [ngModelOptions]="{standalone:true}">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>City</mat-label>
                <input matInput [(ngModel)]="cs.city" [ngModelOptions]="{standalone:true}">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>State</mat-label>
                <input matInput [(ngModel)]="cs.state" [ngModelOptions]="{standalone:true}">
              </mat-form-field>
              <mat-form-field appearance="outline" class="field-full">
                <mat-label>Diagnosis</mat-label>
                <input matInput [(ngModel)]="cs.diagnosis" [ngModelOptions]="{standalone:true}">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Hospital</mat-label>
                <input matInput [(ngModel)]="cs.hospital" [ngModelOptions]="{standalone:true}">
              </mat-form-field>
            </div>

            <mat-form-field appearance="outline" class="field-full">
              <mat-label>Background Notes (context for AI story)</mat-label>
              <textarea matInput rows="3" [(ngModel)]="cs.backgroundNotes" [ngModelOptions]="{standalone:true}"
                placeholder="e.g. Dreamed of being a police officer, diagnosed with Aplastic Anemia, brother was BMT donor..."></textarea>
            </mat-form-field>

            <button mat-raised-button color="accent" type="button" (click)="generateCaseStudyStory(cs)" [disabled]="cs.generating">
              <mat-icon>{{cs.generating ? 'hourglass_empty' : 'auto_awesome'}}</mat-icon>
              {{cs.generating ? 'Generating...' : 'Generate AI Story'}}
            </button>

            <div class="story-editor-section" *ngIf="cs.paragraphs.length">
              <div *ngFor="let p of cs.paragraphs; let pi = index" class="bullet-row">
                <mat-form-field appearance="outline" class="field-full">
                  <textarea matInput rows="3" [(ngModel)]="cs.paragraphs[pi]" [ngModelOptions]="{standalone:true}"></textarea>
                </mat-form-field>
                <button mat-icon-button color="warn" type="button" (click)="removeCaseStudyParagraph(cs, pi)"><mat-icon>remove_circle</mat-icon></button>
              </div>
              <button mat-button type="button" (click)="addCaseStudyParagraph(cs)"><mat-icon>add</mat-icon> Add Paragraph</button>
            </div>

            <div class="photo-upload-section">
              <p class="upload-label">Photo</p>
              <input type="file" accept="image/*" (change)="uploadCaseStudyPhoto($event, cs)" #csPhotoInput hidden>
              <button mat-stroked-button color="accent" type="button" (click)="csPhotoInput.click()" [disabled]="cs.uploadingPhoto">
                <mat-icon>{{cs.uploadingPhoto ? 'hourglass_empty' : 'upload_file'}}</mat-icon>
                {{cs.uploadingPhoto ? 'Uploading…' : 'Upload Photo'}}
              </button>
              <div class="photo-preview-row" *ngIf="cs.photo">
                <div class="photo-thumb"><img [src]="cs.photo" alt="case study"></div>
              </div>
            </div>

          </mat-card-content>
        </mat-card>
      </div>

      <div class="step-actions">
        <button mat-button matStepperPrevious type="button">Back</button>
        <button mat-raised-button color="primary" matStepperNext type="button">
          Next: Generate PDF <mat-icon>picture_as_pdf</mat-icon>
        </button>
      </div>
    </mat-step>

    <!-- ═══════════════════════════════════════════════════════
         STEP 6 — Generate PDF
    ═══════════════════════════════════════════════════════ -->
    <mat-step label="Generate PDF">
      <h3 class="step-title">Generate Narrative Report PDF</h3>
      <div class="help-note">
        <mat-icon>info</mat-icon>
        <ul>
          <li>Switch off any section you don’t want in the PDF under <strong>Sections to include</strong>.</li>
          <li>Click <strong>Generate Report PDF</strong>, check the preview below, then use <strong>Print / Save as PDF</strong> (choose “Save as PDF” in the print dialog).</li>
          <li><strong>Save Draft</strong> stores your progress without generating the PDF.</li>
        </ul>
      </div>

      <mat-card class="summary-card">
        <mat-card-content>
          <div class="summary-row"><strong>Centre:</strong> {{configForm.value.centreName || configForm.value.centreCode}}</div>
          <div class="summary-row"><strong>Quarter:</strong> {{configForm.value.quarterLabel}} ({{periodLabel}})</div>
          <div class="summary-row"><strong>Case Studies:</strong> {{caseStudies.length}}</div>
          <div class="summary-row"><strong>Education Photos:</strong> {{eduSessionPhotos.length + eduActivityPhotos.length + eduVolunteerPhotos.length}}</div>
          <div class="summary-row"><strong>Counselling Photos:</strong> {{cslSessionPhotos.length + cslGroupPhotos.length}}</div>
        </mat-card-content>
      </mat-card>

      <mat-card class="summary-card">
        <mat-card-title>Sections to include in the PDF</mat-card-title>
        <mat-card-content>
          <div class="section-toggles">
            <mat-slide-toggle *ngFor="let opt of sectionOptions" color="primary" [(ngModel)]="sections[opt.key]">
              {{opt.label}}
            </mat-slide-toggle>
          </div>
        </mat-card-content>
      </mat-card>

      <div class="generate-btn-area">
        <div class="generate-row">
          <button mat-raised-button color="primary" type="button" (click)="generateAndPrint()"
            [disabled]="generatingPdf || saving" class="generate-pdf-btn">
            <mat-icon>{{generatingPdf ? 'hourglass_empty' : 'picture_as_pdf'}}</mat-icon>
            {{generatingPdf ? 'Generating…' : 'Generate Report PDF'}}
          </button>

          <button mat-stroked-button color="accent" type="button" (click)="saveReport()"
            [disabled]="saving || generatingPdf" class="save-draft-btn">
            <mat-icon>{{saving ? 'hourglass_empty' : 'save'}}</mat-icon>
            {{saving ? 'Saving…' : 'Save Draft'}}
          </button>
        </div>

        <p class="saved-status" *ngIf="savedAt">
          <mat-icon>check_circle</mat-icon>
          Last saved: {{savedAt | date:'d MMM yyyy, h:mm a'}}
          <span *ngIf="reportId" class="report-id">· ID: {{reportId}}</span>
        </p>

        <mat-progress-bar *ngIf="generatingPdf" mode="indeterminate" style="width:320px;margin-top:8px;"></mat-progress-bar>
      </div>

      <!-- ── Inline Preview ── -->
      <div *ngIf="previewHtml" class="preview-wrapper">
        <div class="preview-toolbar">
          <mat-icon class="preview-icon">preview</mat-icon>
          <span class="preview-label">Report Preview — check page breaks before printing</span>
          <div class="preview-actions">
            <button mat-raised-button color="primary" type="button" (click)="printReport()">
              <mat-icon>print</mat-icon> Print / Save as PDF
            </button>
            <button mat-icon-button matTooltip="Regenerate" type="button" (click)="previewHtml = ''; generateAndPrint()">
              <mat-icon>refresh</mat-icon>
            </button>
            <button mat-icon-button matTooltip="Close preview" type="button" (click)="previewHtml = ''">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>
        <iframe id="narrativeReportPreviewFrame" class="report-preview-frame"></iframe>
        <div class="preview-footer">
          Press <strong>Ctrl+P</strong> (or <strong>⌘+P</strong> on Mac) → <strong>Save as PDF</strong> in the print dialog
        </div>
      </div>

      <div class="step-actions">
        <button mat-button matStepperPrevious type="button">Back</button>
      </div>
    </mat-step>

  </mat-stepper>
</div>
